import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, ImageIcon, UserIcon, CalendarIcon, DollarSignIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import ProfileLayout, { ProfileData } from '../../components/Profile/ProfileLayout';
import { PortfolioTab, ServicesTab, ReviewsTab, AboutTab, EventsTab, PortfolioItem, ServiceTier, Review, Event, ProcessStep } from '../../components/Profile/ProfileTabs';
// --- Mock Data ---
const creativeProfile: ProfileData = {
  id: 'c1',
  name: 'Alex Rivera',
  role: 'Cover Designer & Illustrator',
  location: 'Portland, OR',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  coverImage: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
  status: 'Available for Work',
  queueStatus: 'open',
  slotsAvailable: 3,
  maxSlots: 5,
  estimatedWait: '2-3 weeks',
  rating: 4.9,
  bio: 'I specialize in crafting immersive book covers for Fantasy and Sci-Fi authors. My work blends digital painting with custom typography to create worlds that readers want to step into. With a background in traditional fine arts and 7 years of industry experience, I understand how to make a book stand out on a crowded shelf—digital or physical.',
  specialties: ['Fantasy', 'Sci-Fi', 'Typography', 'Hand-Lettering'],
  stats: [{
    label: 'Rating',
    value: 4.9
  }, {
    label: 'Projects',
    value: 127
  }, {
    label: 'Reviews',
    value: 42
  }, {
    label: 'Followers',
    value: '8.5k'
  }, {
    label: 'Years Exp',
    value: '7+'
  }],
  social: {
    website: 'https://alexriveradesign.com',
    instagram: '@alexrivera_art',
    twitter: '@arivera_design',
    behance: 'behance.net/alexrivera'
  }
};
const skills = ['Photoshop', 'Illustrator', 'Procreate', 'InDesign', 'Digital Painting', '3D Modeling'];
const tools = ['Adobe Creative Suite', 'Procreate', 'Blender', 'Figma'];
const processSteps: ProcessStep[] = [{
  title: 'Discovery',
  description: 'Manuscript review & mood boards'
}, {
  title: 'Concepts',
  description: '2-3 rough sketches & composition options'
}, {
  title: 'Refinement',
  description: 'Detailed rendering of chosen direction'
}, {
  title: 'Typography',
  description: 'Custom title treatment & layout'
}, {
  title: 'Delivery',
  description: 'Final files in all required formats'
}];
const willDo = ['Fantasy', 'Sci-Fi', 'Young Adult', 'Romance', 'Literary Fiction'];
const wontDo = ['Horror', 'Erotica', 'Fan Art of existing IP', 'Rush jobs under 1 week'];
const inspiration = ['Frank Frazetta', 'Classic 70s Sci-Fi Art', 'Art Deco Architecture', 'Nature textures'];
const funFacts = ['I collect vintage sci-fi paperbacks solely for the cover art.', 'I have a golden retriever named Pixel.', 'I sketch every day before touching a computer.', 'My coffee order is a triple shot latte, no sugar.'];
const portfolioItems: PortfolioItem[] = [{
  id: 'p1',
  title: 'The Starless Sea',
  category: 'Cover Design',
  imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Full jacket design for a YA Fantasy bestseller featuring custom gold foil typography.',
  aspectRatio: 'portrait',
  serviceCategory: 'Cover Design'
}, {
  id: 'p2',
  title: 'Neon Gods',
  category: 'Illustration',
  imageUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Character illustrations for a Cyberpunk anthology series.',
  aspectRatio: 'landscape',
  serviceCategory: 'Illustration'
}, {
  id: 'p3',
  title: 'Velvet & Steel',
  category: 'Typography',
  imageUrl: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Custom hand-lettered title treatment for a historical romance novel.',
  aspectRatio: 'square',
  serviceCategory: 'Cover Design'
}, {
  id: 'p4',
  title: 'Echoes of Void',
  category: 'Cover Design',
  imageUrl: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Minimalist sci-fi cover focusing on negative space and scale.',
  aspectRatio: 'portrait',
  serviceCategory: 'Cover Design'
}, {
  id: 'p5',
  title: "The Alchemist's Daughter",
  category: 'Branding',
  imageUrl: 'https://images.unsplash.com/photo-1634152962476-4b8a00e1915c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Series branding and logo design for a 5-book fantasy epic.',
  aspectRatio: 'landscape',
  serviceCategory: 'Cover Design'
}, {
  id: 'p6',
  title: 'Midnight Garden',
  category: 'Illustration',
  imageUrl: 'https://images.unsplash.com/photo-1629196914375-f7e48f477b6d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Intricate floral illustrations for chapter headers and endpapers.',
  aspectRatio: 'square',
  serviceCategory: 'Illustration'
}, {
  id: 'p7',
  title: 'Solar Flare',
  category: 'Cover Design',
  imageUrl: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'High-contrast space opera cover art using 3D modeled elements.',
  aspectRatio: 'portrait',
  serviceCategory: 'Cover Design'
}, {
  id: 'p8',
  title: 'Rogue Protocol',
  category: 'Typography',
  imageUrl: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  description: 'Glitch-effect typography for a techno-thriller series.',
  aspectRatio: 'landscape',
  serviceCategory: 'Cover Design'
}];
const services: ServiceTier[] = [{
  id: 's1',
  name: 'eBook Essentials',
  price: '$250',
  description: 'Perfect for indie authors launching their first digital release.',
  features: ['High-res eBook Cover (JPG)', '3D Book Mockup', 'Stock Photo Manipulation', 'Standard Typography'],
  turnaround: '2 weeks',
  revisions: '2 rounds',
  category: 'Cover Design'
}, {
  id: 's2',
  name: 'The Full Wrap',
  price: '$450',
  description: 'Complete print-ready design for paperback and hardcover editions.',
  isPopular: true,
  features: ['Everything in eBook Essentials', 'Print-ready PDF (Front, Back, Spine)', 'Custom Title Typography', 'Social Media Promo Kit', 'IngramSpark & KDP Compliant'],
  turnaround: '3-4 weeks',
  revisions: '3 rounds',
  category: 'Cover Design'
}, {
  id: 's3',
  name: 'World Builder',
  price: '$750+',
  description: 'A comprehensive visual identity for your book or series.',
  features: ['Everything in Full Wrap', 'Custom Illustration / Digital Painting', 'Audiobook Cover Adaptation', 'Bookmark & Merch Designs', 'Series Branding Guide'],
  turnaround: '5-6 weeks',
  revisions: 'Unlimited',
  category: 'Cover Design'
}, {
  id: 's4',
  name: 'Character Art',
  price: '$300',
  description: 'Detailed character portraits for marketing or interior art.',
  features: ['Full body or bust', 'Simple background', 'High-res digital file', 'Commercial usage rights'],
  turnaround: '2-3 weeks',
  revisions: '2 rounds',
  category: 'Illustration'
}];
const reviews: Review[] = [{
  id: 'r1',
  clientName: 'Sarah J. Maas',
  clientRole: 'Fantasy Author',
  clientAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
  rating: 5,
  comment: 'Alex completely understood the vibe I was going for. The cover captures the magic system perfectly without being cliché. Absolutely stunning work.',
  date: 'Oct 2023',
  projectType: 'Cover Design',
  completionTime: '3 weeks'
}, {
  id: 'r2',
  clientName: 'James Corey',
  clientRole: 'Sci-Fi Author',
  clientAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
  rating: 5,
  comment: 'Professional, timely, and incredibly talented. The typography work alone is worth the price. My pre-orders doubled after the cover reveal.',
  date: 'Aug 2023',
  projectType: 'Full Wrap',
  completionTime: '4 weeks'
}, {
  id: 'r3',
  clientName: 'Elena Richards',
  clientRole: 'Thriller Author',
  clientAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
  rating: 4,
  comment: "Great communication throughout the process. Alex was patient with my revisions and delivered a final product that I'm proud to display.",
  date: 'July 2023',
  projectType: 'eBook Cover',
  completionTime: '12 days'
}, {
  id: 'r4',
  clientName: 'Michael Chen',
  clientRole: 'Indie Author',
  clientAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
  rating: 5,
  comment: 'The branding package Alex created for my series has given it such a professional edge. Highly recommended!',
  date: 'June 2023',
  projectType: 'Branding',
  completionTime: '5 weeks'
}];
const events: Event[] = [{
  id: 'e1',
  title: 'Cover Design Masterclass',
  type: 'Workshop',
  date: 'Nov 15, 2023',
  time: '10:00 AM - 2:00 PM',
  location: 'Virtual (Zoom)',
  description: 'Learn the fundamentals of composition and typography for genre fiction covers. Suitable for beginners and intermediate designers.'
}, {
  id: 'e2',
  title: 'Portfolio Reviews',
  type: 'Mentoring',
  date: 'Dec 05, 2023',
  time: '1:00 PM - 4:00 PM',
  location: 'Portland Art Center',
  description: 'One-on-one portfolio critique sessions for aspiring book cover designers. Bring your physical or digital portfolio.'
}];
const similarCreatives = [{
  id: 'sc1',
  name: 'Mia Chen',
  role: 'Illustrator',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
  rating: 4.8,
  specialty: 'Character Art'
}, {
  id: 'sc2',
  name: 'David Park',
  role: 'Typographer',
  avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200',
  rating: 5.0,
  specialty: 'Hand Lettering'
}, {
  id: 'sc3',
  name: 'Sophie Williams',
  role: 'Cover Designer',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200',
  rating: 4.9,
  specialty: 'Romance Covers'
}, {
  id: 'sc4',
  name: 'Marcus Johnson',
  role: 'Concept Artist',
  avatar: 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=200',
  rating: 4.7,
  specialty: 'World Building'
}];
const CreativeProfile = () => {
  const {
    darkMode
  } = useDarkMode();
  const [activeTab, setActiveTab] = useState('portfolio');
  const tabs = [{
    id: 'portfolio',
    label: 'Portfolio',
    icon: ImageIcon,
    count: portfolioItems.length
  }, {
    id: 'about',
    label: 'About',
    icon: UserIcon
  }, {
    id: 'services',
    label: 'Services',
    icon: DollarSignIcon,
    count: services.length
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: StarIcon,
    count: reviews.length
  }, {
    id: 'events',
    label: 'Events',
    icon: CalendarIcon,
    count: events.length
  }];
  const SimilarProfiles = <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide" data-id="element-9284" data-mp="1vtyxplilpxhj">
      {similarCreatives.map(creative => <Link key={creative.id} to="#" className={`flex-shrink-0 w-64 p-4 rounded-xl border transition-all hover:shadow-md ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-9285" data-mp="1xcgqin5ffnhl">
          <div className="flex items-center gap-4 mb-3" data-id="element-9286" data-mp="1t90zq7g1ghct">
            <img src={creative.avatar} alt={creative.name} className="w-14 h-14 rounded-full object-cover" data-id="element-9287" data-mp="1tbn4jzbjoirc" />

            <div data-id="element-9288" data-mp="1jceehhjsvoa9">
              <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9289" data-mp="1jcxmo6jneyb3">
                {creative.name}
              </h3>
              <div className="flex items-center gap-1 text-xs text-yellow-400" data-id="element-9290" data-mp="19mmiciw1yn3o">
                <StarIcon className="w-3 h-3 fill-current" data-id="element-9291" data-mp="vls9jz53ek4h" />
                <span data-id="element-9292" data-mp="10l54l764axi5">{creative.rating}</span>
              </div>
            </div>
          </div>
          <div className={`flex justify-between items-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9293" data-mp="10mzhpzb5dlbo">
            <span data-id="element-9294" data-mp="c1bjb6lxmqvh">{creative.role}</span>
            <span className={`px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-9295" data-mp="c2cvjeeopjes">
              {creative.specialty}
            </span>
          </div>
        </Link>)}
    </div>;
  return <ProfileLayout profile={creativeProfile} tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} similarProfiles={SimilarProfiles} data-id="element-9296" data-mp="zxpdhittya56">
      {activeTab === 'portfolio' && <PortfolioTab items={portfolioItems} services={services} data-id="element-9297" data-mp="19mz38s17bgq4" />}

      {activeTab === 'about' && <AboutTab profile={creativeProfile} skills={skills} tools={tools} processSteps={processSteps} willDo={willDo} wontDo={wontDo} inspiration={inspiration} funFacts={funFacts} data-id="element-9298" data-mp="14q8qoeolv39g" />}

      {activeTab === 'services' && <ServicesTab services={services} data-id="element-9299" data-mp="385aw8pkxvojf" />}

      {activeTab === 'reviews' && <ReviewsTab reviews={reviews} data-id="element-9300" data-mp="3d45i60e7dt7r" />}

      {activeTab === 'events' && <EventsTab events={events} data-id="element-9301" data-mp="3hvne2a1dd3lv" />}
    </ProfileLayout>;
};
export default CreativeProfile;